/**
 * Department Production Entry (Feb 2026)
 * --------------------------------------
 * Operator-facing shop-floor entry screen. Four bold tabs — Warping,
 * Covering, Weaving, Finishing — each optimized for a single fast
 * workflow:
 *
 *   1. Tap a Department tab.
 *   2. Pick an SO/ESO from a large picker.
 *   3. Pick the Child Work Order (auto-filtered to that SO + Dept).
 *   4. Enter today's produced qty per RM line and Save.
 *
 * Compared to the full Preparatory entry screen, this view intentionally
 * skips reverse-consumption, SF pickers and other planner-facing knobs
 * so operators only see what they need. Recent shift entries render as
 * cards beneath the form for a quick review-and-reverse workflow.
 */
import { useSortableRows, SortableTH } from "@/lib/sortable.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import {
  FactoryIcon, CheckCircleIcon, ArrowsClockwiseIcon, TrashIcon,
  ClockCounterClockwiseIcon, ArrowCounterClockwiseIcon,
} from "@phosphor-icons/react";

const DEPTS = [
  { code: "WARPING",         label: "Warping",         color: "#22d3ee" },
  { code: "SPANDEX_WARPING", label: "Spandex Warping", color: "#fbbf24" },
  { code: "COVERING",        label: "Covering",        color: "#f472b6" },
  { code: "WEAVING",         label: "Weaving",         color: "#60a5fa" },
  { code: "FINISHING",       label: "Finishing",       color: "#a78bfa" },
];
// P17-UAT-PRODUCTION-UI-02 · explicit per-process identity (never the ambiguous
// operator-facing "Department Production Entry" once a dedicated screen is used).
const PROCESS_TITLE = {
  WARPING: "Warping Production Entry",
  COVERING: "Covering Production Entry",
  SPANDEX_WARPING: "Spandex Warping Production Entry",
  WEAVING: "Loom Production Entry",
  FINISHING: "Finishing Production Entry",
};
// Feb 2026 · Widened to include "Pending" so operators can post
// production even on preparatory stages that haven't been flipped to
// Ready yet (Spandex Warping / Warping / Covering have no upstream deps
// and the backend safely handles the first entry — recomputing the CWO
// status to In Progress on save). This unblocks the "All CWOs blocked"
// state on SO/ESO/WO pickers reported from the shop floor.
const ENTERABLE = new Set(["Ready", "In Progress", "Pending"]);
// PMS.V3 WO-PRIMARY-SOURCE Phase 1 (Jun 2026) · the three preparatory stages
// whose operator flow is WO-first (WO → read-only SO/ESO → process CWO → read-only
// Product). Finishing keeps its own WO-first block; Weaving/others (dead routes
// here) keep the legacy SO-first fallback. Presentation/selector only — posting
// stays bound to cwo_id via POST /cwo-production-entries (unchanged).
const WO_PRIMARY = new Set(["WARPING", "COVERING", "SPANDEX_WARPING"]);
// WO-PRIMARY Phase 1A.1 · HEADER FG resolver — returns ONLY the authoritative
// parent Finished-Good product_code from the Work Order lineage. It must NEVER
// return a process/SFG code (output_sfw/sfc/sfsw_code · sf_code · rm_code) — the
// FG header identity is strictly separate from the process-table material codes.
// Returns "__MULTI__" when a WO legitimately carries multiple distinct FGs.
function resolveFgProductCode(wo) {
  if (!wo) return "";
  const set = new Set();
  for (const it of (wo.items || [])) {
    const v = it.product_code || it.item_code || it.article_no;
    if (v) set.add(v);
  }
  if (set.size === 1) return [...set][0];
  if (set.size > 1) return "__MULTI__";
  return wo.item_code || wo.product_code || wo.article_no || "";
}
const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const num = (v) => Number(v || 0);

export default function DepartmentProductionEntry({ lockedDept } = {}) {
  const [dept, setDept] = useState(lockedDept || "WARPING");
  const [cwos, setCwos] = useState([]);
  const [entries, setEntries] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [entryDate, setEntryDate] = useState(today);
  const [shift, setShift] = useState("A");
  const [operatorId, setOperatorId] = useState("");
  const [soId, setSoId] = useState("");
  const [woId, setWoId] = useState("");
  const [cwoId, setCwoId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lineQtys, setLineQtys] = useState({});
  // Mar 2, 2026 · Per-line wastage — moved out of the global bottom strip
  // so operators record wastage next to each Produced Qty. Total wastage
  // (Σ lineWastage) still drives the Actual Wastage % roll-up.
  const [lineWastage, setLineWastage] = useState({});
  // Feb 2026 · Phase 2 · Wastage capture + allowance approval
  const [wastageQty, setWastageQty] = useState("");
  const [wastageReason, setWastageReason] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [approvalReason, setApprovalReason] = useState("");
  // Mar 2, 2026 · Received-qty gating — operators can only consume what
  // the current department's floor has actually received from RM Store
  // (or Stage-Transfer-In from the previous floor). Fed by /godown-balances
  // filtered to godowns whose `department` matches the active dept.
  const [godowns, setGodowns] = useState([]);
  const [godownBalances, setGodownBalances] = useState([]);
  // Mar 3, 2026 · Item-Centric Wastage — cumulative wastage per RM
  // across ALL history (all SOs, all stages). Feeds the "Item Waste ∑"
  // column so operators see problem yarns instantly.
  const [rmWasteMap, setRmWasteMap] = useState({});

  // Sep 2026 · Phase 3J.17 · Covering output-driven entry — operator
  // enters ONE SFC produced qty; backend re-derives L2 BOM auto
  // consumption authoritatively. `coveringWastage` is a
  // { rm_code: kg } map for optional Model A wastage per L2 line.
  const [coveringProducedQty, setCoveringProducedQty] = useState("");
  const [coveringWastage, setCoveringWastage] = useState({});
  // P17-UAT-13A REV-D · V2-authoritative Covering-floor availability per RM.
  // Covering posts canonically V2-only, so legacy godown balances are NOT
  // authoritative here — this map (from the same authority the backend enforces)
  // drives the Available column, Balance After preview and Save feasibility.
  const [coveringV2Avail, setCoveringV2Avail] = useState({});
  // P17-UAT-13B REV-A · CH-32 RC2+RC3 · Authoritative Spandex Warping identities
  // (input SFC / output SFSW / batches) + V2 input-SFC availability.
  const [spandexInfo, setSpandexInfo] = useState(null);

  // P17-UAT-16B-1 · Finishing WO-first authority. Finishing (only) selects a
  // Work Order first; Item/Product derive from WO.items[]; SO/ESO is read-only
  // reference; Machine (Finishing dept) is mandatory. Other departments keep
  // their SO→CWO flow untouched.
  const [workOrders, setWorkOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [finWoId, setFinWoId] = useState("");
  const [finItemId, setFinItemId] = useState("");
  const [finMachineId, setFinMachineId] = useState("");
  const [finBasis, setFinBasis] = useState("WEIGHT");   // WEIGHT | LENGTH
  const [finPrimary, setFinPrimary] = useState("");      // operator-entered primary (kg in WEIGHT, m in LENGTH)
  const [finBom, setFinBom] = useState(null);            // { bom_standard_gpm, bom_version, ok }
  const [finBomLoading, setFinBomLoading] = useState(false);
  // P17-UAT-16B-3 · Finishing stock integration — explicit source batch, process
  // loss, auto-resolved destination.
  const [finSources, setFinSources] = useState([]);      // eligible Woven-SFG source buckets
  const [finSourceKey, setFinSourceKey] = useState("");   // `${batch}||${godown_id}`
  const [finLoss, setFinLoss] = useState("");             // process loss kg
  // P17-UAT-16B-3 REV-A · NEXT-process WIP destination (routing-driven; operator-selected)
  const [finDestOptions, setFinDestOptions] = useState([]);
  const [finDestId, setFinDestId] = useState("");
  const [finNext, setFinNext] = useState(null);           // next process code e.g. CHECKING_PACKING
  const [finDestOk, setFinDestOk] = useState(false);
  const [finDestReason, setFinDestReason] = useState("");
  const [finPosting, setFinPosting] = useState(false);
  const finPostRef = useRef(false);                       // true → post to stock after save
  const [editingPeId, setEditingPeId] = useState("");     // 16B-3R · Open/Continue a saved draft (no duplicate PE)
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: e }, { data: op }, { data: g }, { data: gb }, { data: ws }, { data: wo }, { data: mc }] = await Promise.all([
        api.get("/child-work-orders"),
        api.get(`/cwo-production-entries?stage_code=${dept}`),
        api.get("/operators/"),
        api.get("/godowns/"),
        api.get("/godown-balances"),
        api.get("/reports/rm-wastage-summary"),
        api.get("/work-orders/").catch(() => ({ data: [] })),
        api.get("/machines/").catch(() => ({ data: [] })),
      ]);
      setCwos(c || []);
      setEntries(e || []);
      setOperators(op || []);
      setGodowns(g || []);
      setGodownBalances(gb || []);
      setWorkOrders(wo || []);
      setMachines(mc || []);
      const map = {};
      for (const row of ws || []) {
        map[row.rm_code] = {
          total: Number(row.total_wastage_qty || 0),
          excess: Number(row.total_excess_qty || 0),
          pct: Number(row.avg_wastage_pct || 0),
          count: Number(row.entry_count || 0),
        };
      }
      setRmWasteMap(map);
      return c || [];
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dept]);

  // Cascading pickers · only Ready / In Progress CWOs are enterable so
  // operators can't post against a stage whose predecessors are still
  // Pending (matches the routing guardrail from Preparatory entry).
  const soOptions = useMemo(() => {
    const seen = new Map();
    for (const c of cwos) {
      if (c.stage_code !== dept) continue;
      if (!ENTERABLE.has(c.status)) continue;
      const key = c.source_order_id || "";
      if (!key || seen.has(key)) continue;
      seen.set(key, {
        id: c.source_order_id,
        no: c.source_order_no || "(no ref)",
        type: c.source_type || "SO",
        customer: c.customer_name || "",
      });
    }
    return Array.from(seen.values()).sort((a, b) => (a.no || "").localeCompare(b.no || ""));
  }, [cwos, dept]);

  // Feb 2026 · Only CWOs whose deps are still Pending AND that have a
  // real depends_on chain count as "blocked" — preparatory CWOs with no
  // deps just sit as Pending until first entry, and shouldn't appear
  // blocked from the operator's perspective.
  const blockedCount = useMemo(
    () => cwos.filter(c => c.stage_code === dept && c.status === "Pending" && (c.depends_on || "").trim()).length,
    [cwos, dept],
  );

  const cwoOptions = useMemo(() => {
    const base = cwos.filter(c => c.stage_code === dept && ENTERABLE.has(c.status));
    // WO-PRIMARY depts scope CWOs to the selected Work Order (cross-WO
    // protection — a CWO from another WO can NEVER surface). Legacy fallback
    // depts keep the SO/ESO scoping.
    const scoped = WO_PRIMARY.has(dept)
      ? base.filter(c => woId && c.parent_wo_id === woId)
      : base.filter(c => !soId || c.source_order_id === soId);
    return scoped.sort((a, b) => (a.cwo_no || "").localeCompare(b.cwo_no || ""));
  }, [cwos, dept, soId, woId]);
  const pickedCwo = useMemo(() => cwoOptions.find(c => c.id === cwoId), [cwoOptions, cwoId]);
  // WO-PRIMARY · eligible Work Orders for the active stage are derived from the
  // SAME authority as before — the set of enterable CWOs at this stage — grouped
  // by their parent WO. No new WO eligibility rule is introduced.
  const woOptions = useMemo(() => {
    const map = new Map();
    for (const c of cwos) {
      if (c.stage_code !== dept) continue;
      if (!ENTERABLE.has(c.status)) continue;
      const key = c.parent_wo_id || "";
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, { id: key, no: c.parent_wo_no || "(WO)", source_type: c.source_type || "SO",
                       source_no: c.source_order_no || "", source_id: c.source_order_id || "" });
      }
    }
    return Array.from(map.values())
      .map(o => {
        // FG comes ONLY from the authoritative parent WO lineage — never a
        // process/SFG code off the CWO.
        const woRec = workOrders.find(w => w.id === o.id);
        const fg = resolveFgProductCode(woRec);
        const fgLabel = fg === "__MULTI__" ? "Multiple Products" : fg;
        return { ...o, fg, fgLabel };
      })
      .sort((a, b) => (a.no || "").localeCompare(b.no || ""));
  }, [cwos, dept, workOrders]);
  const selectedWo = useMemo(() => woOptions.find(o => o.id === woId), [woOptions, woId]);
  const headerFg = selectedWo ? (selectedWo.fgLabel || "") : "";
  const woSourceTypeLabel = (t) => {
    const s = String(t || "").toLowerCase();
    return (s === "export_order" || s === "eso") ? "ESO" : "SO";
  };

  // CASE A · exactly one eligible CWO → auto-select. CASE C · zero → clear.
  // CASE B · multiple → leave to the operator (controlled selector in the UI).
  useEffect(() => {
    if (!WO_PRIMARY.has(dept) || !woId) return;
    if (cwoOptions.length === 1 && cwoId !== cwoOptions[0].id) setCwoId(cwoOptions[0].id);
    else if (cwoOptions.length === 0 && cwoId) setCwoId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dept, woId, cwoOptions]);

  // WO change · immediately clear every dependent selection so no CWO / product /
  // material context from the previous WO can ever remain active.
  const changeWo = (id) => {
    setWoId(id);
    const o = woOptions.find(w => w.id === id);
    setSoId(o?.source_id || "");
    setCwoId("");
    setLineQtys({}); setLineWastage({});
    setCoveringProducedQty(""); setCoveringWastage({});
    setCoveringV2Avail({}); setSpandexInfo(null);
  };

  // WO-PRIMARY Phase 1A · Refresh re-fetches Work Orders + eligible CWOs and
  // re-validates the current selection against the refreshed eligibility —
  // keeping the selected WO only if it is still eligible, else clearing it
  // safely. Non-mutating (no POST / no WO/CWO/stock change).
  const refresh = async () => {
    const fresh = await load();
    if (woId && WO_PRIMARY.has(dept)) {
      const stillEligible = (fresh || []).some(
        c => c.stage_code === dept && ENTERABLE.has(c.status) && c.parent_wo_id === woId,
      );
      if (!stillEligible) { changeWo(""); toast.info("Selected Work Order is no longer eligible — cleared"); }
    }
  };

  // ── Finishing WO-first derived values ──────────────────────────────────
  const finWorkOrders = useMemo(
    () => (workOrders || []).filter(w => {
      const st = (w.status || "").toLowerCase();
      return st !== "cancelled" && st !== "draft";  // eligible = approved/active per existing status data
    }).sort((a, b) => (b.po_no || "").localeCompare(a.po_no || "")),
    [workOrders],
  );
  const finWo = useMemo(() => (workOrders || []).find(w => w.id === finWoId), [workOrders, finWoId]);
  const finItems = useMemo(() => {
    const its = (finWo?.items || []).filter(li => li && (li.item_id || li.product_code));
    return its;
  }, [finWo]);
  const finItem = useMemo(
    () => finItems.find(li => (li.item_id || li.product_code) === finItemId),
    [finItems, finItemId],
  );
  const finMachines = useMemo(
    () => (machines || []).filter(m => (m.department || "").toLowerCase() === "finishing"),
    [machines],
  );
  const finProduct = finItem?.product_code || finItem?.item_code || "";
  const finSoRef = finWo ? `${(finWo.source_type || "SO").toUpperCase()} · ${finWo.source_order_no || "—"}` : "";
  // Auto-select the only item on a single-item WO.
  useEffect(() => {
    if (dept !== "FINISHING") return;
    if (finItems.length === 1) {
      const only = finItems[0];
      setFinItemId(only.item_id || only.product_code || "");
    } else if (finItems.length === 0) {
      setFinItemId("");
    }
  }, [dept, finWoId, finItems]);

  // 16B-2 · Resolve approved BOM Standard g/m whenever the Finishing Product changes.
  const round3 = (v) => Math.round((Number(v) || 0) * 1000) / 1000;
  useEffect(() => {
    if (dept !== "FINISHING" || !finProduct) { setFinBom(null); return; }
    let alive = true;
    setFinBomLoading(true);
    api.get(`/production-entries/finishing/bom-weight?product_code=${encodeURIComponent(finProduct)}`)
      .then(r => { if (alive) setFinBom(r.data || null); })
      .catch(() => { if (alive) setFinBom({ ok: false }); })
      .finally(() => { if (alive) setFinBomLoading(false); });
    return () => { alive = false; };
  }, [dept, finProduct]);

  const finGpm = num(finBom?.bom_standard_gpm);
  const finPrimaryNum = num(finPrimary);
  const finCalcKg = finBasis === "WEIGHT" ? finPrimaryNum : (finGpm > 0 ? round3(finPrimaryNum * finGpm / 1000) : 0);
  const finCalcM = finBasis === "LENGTH" ? finPrimaryNum : (finGpm > 0 ? round3(finPrimaryNum * 1000 / finGpm) : 0);
  const finBomOk = !!finBom?.ok && finGpm > 0;

  // 16B-3 REV-A · resolve valid NEXT-process WIP destinations for the Finishing
  // Product (routing-driven). Operator must explicitly select when >1 is valid.
  useEffect(() => {
    if (dept !== "FINISHING" || !finProduct) {
      setFinDestOptions([]); setFinNext(null); setFinDestOk(false); setFinDestReason(""); return;
    }
    let alive = true;
    api.get(`/finishing/destination-options?product_code=${encodeURIComponent(finProduct)}`)
      .then(r => {
        if (!alive) return;
        const d = r.data || {};
        setFinDestOk(!!d.ok);
        setFinDestReason(d.reason || "");
        const opts = d.options || [];
        setFinDestOptions(opts);
        setFinNext(d.next_process || null);
        setFinDestId(prev => {
          if (prev && opts.some(o => o.godown_id === prev)) return prev;
          return opts.length === 1 ? opts[0].godown_id : "";
        });
      })
      .catch(() => { if (alive) { setFinDestOptions([]); setFinNext(null); setFinDestOk(false); } });
    return () => { alive = false; };
  }, [dept, finProduct]);

  // 16B-3 · load eligible Woven-SFG source batches for the selected WO + Product.
  useEffect(() => {
    if (dept !== "FINISHING" || !finWoId || !finProduct) { setFinSources([]); setFinSourceKey(""); return; }
    let alive = true;
    api.get(`/finishing/eligible-sources?wo_id=${encodeURIComponent(finWoId)}&product_code=${encodeURIComponent(finProduct)}`)
      .then(r => {
        if (!alive) return;
        const srcs = (r.data?.sources) || [];
        setFinSources(srcs);
        if (srcs.length === 1) setFinSourceKey(`${srcs[0].batch_lot}||${srcs[0].godown_id}`);
        else setFinSourceKey("");
      })
      .catch(() => { if (alive) setFinSources([]); });
    return () => { alive = false; };
  }, [dept, finWoId, finProduct]);

  // 16B-3R · Open/Continue — restore a saved Finishing draft into the form.
  useEffect(() => {
    const peId = searchParams.get("finishingPe");
    if (!peId || !(workOrders || []).length || editingPeId === peId) return;
    (async () => {
      try {
        const { data: pe } = await api.get(`/production-entries/${peId}`);
        if ((pe.stage || "") !== "Finishing") { toast.error("Not a Finishing entry"); return; }
        setDept("FINISHING");
        setFinWoId(pe.work_order_id || "");
        setFinItemId(pe.item_id || "");
        setFinMachineId(pe.machine_id || "");
        setFinBasis(pe.entry_basis || "WEIGHT");
        setFinPrimary(String((pe.entry_basis === "LENGTH" ? pe.production_length_m : pe.production_weight_kg) ?? ""));
        setFinLoss(pe.process_loss_kg != null ? String(pe.process_loss_kg) : "");
        if (pe.source_batch_lot && pe.source_godown_id) setFinSourceKey(`${pe.source_batch_lot}||${pe.source_godown_id}`);
        if (pe.finishing_selected_dest_godown_id) setFinDestId(pe.finishing_selected_dest_godown_id);
        setEditingPeId(peId);
        toast.success(`Opened draft ${pe.entry_no} — review and Post`);
      } catch {
        toast.error("Failed to open draft entry");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrders, searchParams]);

  const finSource = useMemo(
    () => finSources.find(s => `${s.batch_lot}||${s.godown_id}` === finSourceKey),
    [finSources, finSourceKey],
  );
  const finLossNum = Math.max(0, num(finLoss));
  const finInputKg = round3(finCalcKg + finLossNum);                   // input = output + loss
  // P17-UAT-DUAL-UOM-03 · derived read-only metres (same frozen g/m, KG stays authoritative)
  const finInputM = finGpm > 0 ? round3(finInputKg * 1000 / finGpm) : 0;
  const finLossM = finGpm > 0 ? round3(finLossNum * 1000 / finGpm) : 0;
  const finSourceAvail = num(finSource?.available_kg);
  const finStockOk = !!finSource && finInputKg > 0 && finInputKg - finSourceAvail <= 1e-3;
  const finOutBatchPreview = finSource ? `${finSource.batch_lot}/FIN/<PE on save>` : "";

  // P17-UAT-13A REV-D · Fetch V2-authoritative Covering-floor availability for
  // the picked Covering CWO. Cleared for non-Covering depts / no selection.
  useEffect(() => {
    let alive = true;
    if (dept === "COVERING" && pickedCwo?.id) {
      api.get(`/covering-floor-availability?cwo_id=${pickedCwo.id}`)
        .then(({ data }) => {
          if (!alive) return;
          const m = {};
          for (const it of (data?.items || [])) m[it.rm_code] = Number(it.available || 0);
          setCoveringV2Avail(m);
        })
        .catch(() => { if (alive) setCoveringV2Avail({}); });
    } else {
      setCoveringV2Avail({});
    }
    return () => { alive = false; };
  }, [dept, pickedCwo?.id, entries]);

  // P17-UAT-13B REV-A · CH-32 · Fetch authoritative SPX identities + V2 avail.
  useEffect(() => {
    let alive = true;
    if (dept === "SPANDEX_WARPING" && pickedCwo?.id) {
      api.get(`/spandex-floor-availability?cwo_id=${pickedCwo.id}`)
        .then(({ data }) => { if (alive) setSpandexInfo(data); })
        .catch(() => { if (alive) setSpandexInfo(null); });
    } else {
      setSpandexInfo(null);
    }
    return () => { alive = false; };
  }, [dept, pickedCwo?.id, entries]);

  const rmRows = useMemo(() => {
    // Mar 2, 2026 · Available = Received (from RM Store / prev floor) −
    // Consumed. Filter godown_balances to those whose godown's department
    // matches the active dept, then aggregate per rm_code.
    const activeLabel = (DEPTS.find(d => d.code === dept) || {}).label || "";
    const deptGodownIds = new Set(
      (godowns || [])
        .filter(g => (g.department || "").toLowerCase() === activeLabel.toLowerCase())
        .map(g => g.id)
        .filter(Boolean),
    );
    const stockByRm = {};
    for (const b of (godownBalances || [])) {
      if (deptGodownIds.size > 0 && !deptGodownIds.has(b.godown_id)) continue;
      const k = b.rm_code || "";
      if (!k) continue;
      const rec = stockByRm[k] || { received: 0, received_net: 0, consumed_stock: 0, produced: 0, issued: 0 };
      rec.received += Number(b.received || 0);
      // P17-UAT-04 REV-A · net-of-reversal received for operational display.
      // Fail safe when an older response lacks received_net: never over-show
      // reversed material — fall back to the conservative (received − issued).
      rec.received_net += (b.received_net != null)
        ? Number(b.received_net)
        : Math.max(0, Number(b.received || 0) - Number(b.issued || 0));
      rec.consumed_stock += Number(b.consumed || 0);
      rec.produced += Number(b.produced || 0);
      rec.issued += Number(b.issued || 0);
      stockByRm[k] = rec;
    }
    // Phase 3J.9C · SFW Produced to Date per (cwo_id, rm_code) — sums
    // production entries so the operator sees cumulative SFW output for
    // the exact conversion contract. Legacy entries (produced without
    // canonical V2 SFW IN) are included here as-is; the historical
    // truth is what was posted. Spec §10, §23.
    // Phase 3J.15D · Legacy wastage aggregation — sum every cwo_production_entry's
    // `wastage_qty` per rm_code so the Consumed / Balance display can
    // derive historical values for LEGACY_PRE_CANONICAL rows without
    // touching canonical logic (spec §4). `actual_wastage_qty_authoritative`
    // stays 0 on legacy rows because it reads from `wastage_records`
    // which is only populated for canonical postings.
    const sfwByRm = {};
    const legacyWastageByRm = {};
    for (const e of (entries || [])) {
      if (!pickedCwo || e.cwo_id !== pickedCwo.id) continue;
      const k = e.rm_code || "";
      if (!k) continue;
      sfwByRm[k] = (sfwByRm[k] || 0) + Number(e.produced_qty || 0);
      legacyWastageByRm[k] = (legacyWastageByRm[k] || 0) + Number(e.wastage_qty || 0);
    }
    return (pickedCwo?.items || []).map(it => {
      const planned = num(it.qty);
      const produced = num(it.qty_produced);
      const stock = stockByRm[it.rm_code] || { received: 0, received_net: 0, consumed_stock: 0 };
      // P17-UAT-04 REV-A · operational "Received" = net-of-reversal receipt.
      // floor_balance keeps using GROSS received so the reversal isn't
      // double-subtracted (issued already carries the reversal OUT).
      const received_gross = Number(stock.received || 0);
      const received = Number(stock.received_net || 0);
      // Available on floor = (GrossReceived + Produced-on-this-floor) − Consumed-on-this-floor − Issued
      const floor_balance = Math.max(0, received_gross + (stock.produced || 0) - (stock.consumed_stock || 0) - (stock.issued || 0));
      // Cap Consume Now at both plan-remaining AND floor stock actually available.
      const plan_balance = Math.max(0, planned - produced);
      const cap = Math.min(plan_balance, floor_balance);
      const waste = rmWasteMap[it.rm_code] || { total: 0, excess: 0, pct: 0, count: 0 };
      // Phase 3J.9C · Surface Phase 3J.9A stable-ID contract fields on
      // the row so the Warping table can render Input RM · Ends · Output
      // SFW without re-parsing sf_code strings.
      return {
        ...it, planned, produced,
        balance: plan_balance,
        received,
        consumed_floor: Number(stock.consumed_stock || 0) + Number(stock.issued || 0),
        floor_balance,
        cap,
        item_waste_total:  waste.total,
        item_waste_excess: waste.excess,
        item_waste_pct:    waste.pct,
        item_waste_count:  waste.count,
        // Phase 3J.9C · CWO conversion contract (auto-loaded from BOM Explode)
        input_rm_code:   it.input_rm_code   || it.rm_code || "",
        no_of_ends:      Number(it.no_of_ends || 0),
        output_sfw_code: it.output_sfw_code || (it.sf_code && it.sf_code !== it.rm_code ? it.sf_code : ""),
        output_sfw_id:   it.output_sfw_id   || it.sf_article_id || "",
        sfw_produced_to_date: Number(sfwByRm[it.rm_code] || 0),
        // Phase 3J.15D · Legacy historical wastage — sum of
        // `wastage_qty` from cwo_production_entries (source of truth
        // for pre-canonical rows since `wastage_records` was only
        // populated after the canonical migration).
        legacy_wastage_total: Number(legacyWastageByRm[it.rm_code] || 0),
        // Phase 3J.9C.1 · Authoritative consumption from backend read model
        productive_consumed_qty: Number(it.productive_consumed_qty || 0),
        actual_wastage_qty_authoritative: Number(it.actual_wastage_qty || 0),
        has_canonical_consumption_posting: Boolean(it.has_canonical_consumption_posting),
        // Phase 3J.9C.2 · Derived read-model status (spec §12) — feeds
        // the row-level LEGACY badge + Consumed/Available "—" rendering.
        // NONE: no production activity yet on this row (produced = 0).
        // CANONICAL: authoritative PRODUCTION_CONSUMPTION posting exists.
        // LEGACY_PRE_CANONICAL: produced > 0 but no canonical posting.
        consumption_status: Number(sfwByRm[it.rm_code] || 0) <= 0
          ? "NONE"
          : (it.has_canonical_consumption_posting ? "CANONICAL" : "LEGACY_PRE_CANONICAL"),
      };
    });
  }, [pickedCwo, godowns, godownBalances, dept, rmWasteMap, entries]);

  const resetForm = () => {
    setWoId(""); setSoId(""); setCwoId(""); setLineQtys({}); setLineWastage({}); setRemarks("");
    setCoveringProducedQty(""); setCoveringWastage({});
    setFinWoId(""); setFinItemId(""); setFinMachineId(""); setFinBasis("WEIGHT"); setFinPrimary(""); setFinBom(null);
    setFinSourceKey(""); setFinLoss(""); setFinSources([]);
    if (editingPeId) { setEditingPeId(""); setSearchParams({}); }
  };

  const changeDept = (d) => {
    setDept(d);
    resetForm();
  };

  const submit = async () => {
    // P17-UAT-16B-1 · Finishing WO-first production entry (no CWO). Posts via
    // the canonical /production-entries engine (stage=Finishing) which carries
    // entry_no, reversal and audit. Backend independently re-validates
    // WO↔Item↔Product↔SO and the Finishing machine (fail-closed).
    if (dept === "FINISHING") {
      if (!finWoId) return toast.error("Select a Work Order");
      if (!finItem) return toast.error("No valid production item for the selected Work Order");
      if (!finMachineId) return toast.error("Finishing Machine is mandatory");
      if (!finBom?.ok || num(finBom.bom_standard_gpm) <= 0) return toast.error("No valid approved BOM weight for the selected Product");
      const primary = num(finPrimary);
      if (primary <= 0) return toast.error(finBasis === "WEIGHT" ? "Enter production weight (kg)" : "Enter production length (m)");
      const gpm = num(finBom.bom_standard_gpm);
      const kg = finBasis === "WEIGHT" ? primary : round3(primary * gpm / 1000);
      const metres = finBasis === "WEIGHT" ? round3(primary * 1000 / gpm) : primary;
      // 16B-3 · explicit source batch + process loss are required to post to stock.
      const wantPost = finPostRef.current;
      finPostRef.current = false;
      if (wantPost && !finSource) return toast.error("Select an eligible source Woven-SFG batch");
      if (wantPost && finDestOptions.length > 1 && !finDestId)
        return toast.error("Select a destination Packing WIP godown before posting");
      if (wantPost && !finDestId)
        return toast.error("No valid next-process WIP destination resolved for this product");
      if (wantPost && finInputKg - num(finSource?.available_kg) > 1e-3)
        return toast.error("Insufficient source stock for the input quantity");
      // 16B-3R · Open/Continue path — reuse the SAME saved PE (never create a duplicate).
      if (editingPeId) {
        if (!wantPost) return toast.info("Draft already saved — use Save & Post Finishing Stock to post");
        setFinPosting(true);
        try {
          const pr = await api.post(`/production-entries/${editingPeId}/finishing-post`, { dest_godown_id: finDestId });
          toast.success(`Finishing stock posted · output ${pr.data?.output_kg} kg → ${pr.data?.destination_godown} · batch ${pr.data?.output_batch}`);
          setEditingPeId(""); setSearchParams({});
          setFinWoId(""); setFinItemId(""); setFinMachineId(""); setFinBasis("WEIGHT"); setFinPrimary(""); setFinBom(null); setRemarks("");
          setFinSourceKey(""); setFinLoss(""); setFinSources([]);
          load();
        } catch (pe) {
          toast.error(pe?.response?.data?.detail || "Stock post failed");
        } finally {
          setFinPosting(false);
        }
        return;
      }
      try {
        const res = await api.post("/production-entries/", {
          stage: "Finishing",
          entry_date: entryDate, shift, operator_id: operatorId, remarks,
          work_order_id: finWoId,
          item_id: finItem.item_id || "",
          product_code: finProduct,
          order_id: finWo?.source_order_id || "",
          order_kind: (finWo?.source_type || "SO").toUpperCase(),
          linked_source_no: finWo?.source_order_no || "",
          machine_id: finMachineId,
          entry_basis: finBasis,
          production_weight_kg: kg,
          production_length_m: metres,
          produced_meter: metres,
          source_godown_id: finSource?.godown_id || "",
          source_batch_lot: finSource?.batch_lot || "",
          process_loss_kg: finLossNum,
          finishing_selected_dest_godown_id: finDestId || "",
        });
        const peId = res.data?.id;
        if (wantPost && peId) {
          setFinPosting(true);
          try {
            const pr = await api.post(`/production-entries/${peId}/finishing-post`, { dest_godown_id: finDestId });
            toast.success(`Finishing stock posted · output ${pr.data?.output_kg} kg → ${pr.data?.destination_godown} · batch ${pr.data?.output_batch}`);
          } catch (pe) {
            toast.error(pe?.response?.data?.detail || "Stock post failed (entry saved as draft)");
          } finally {
            setFinPosting(false);
          }
        } else {
          toast.success(`Finishing entry recorded · ${finWo?.po_no || "WO"} · ${kg} kg / ${metres} m`);
        }
        setFinWoId(""); setFinItemId(""); setFinMachineId(""); setFinBasis("WEIGHT"); setFinPrimary(""); setFinBom(null); setRemarks("");
        setFinSourceKey(""); setFinLoss(""); setFinSources([]);
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
      return;
    }
    if (!cwoId) return toast.error("Pick a Child WO");

    // Sep 2026 · Phase 3J.17 · Covering output-driven POST
    // Operator enters ONE SFC produced qty; backend covering_output_entry_create
    // re-derives L2 BOM auto consumption authoritatively.
    if (dept === "COVERING") {
      const producedQty = num(coveringProducedQty);
      if (producedQty <= 0) return toast.error("Enter SFC Produced Qty");
      if (!pickedCwo?.output_sfc_id && !(pickedCwo?.items || []).find(it => it.output_sfc_id)) {
        return toast.error("COVERING OUTPUT SF NOT CONFIGURED — re-run Explode BOM");
      }
      // Insufficiency guard using V2-authoritative availability (same authority
      // the backend enforces at posting — DISPLAY = PREVIEW = SAVE = V2).
      const bomBasis = num(pickedCwo?.qty_planned) || 1;
      const preview = (rmRows || []).map(r => {
        const perOutput = num(r.qty) / bomBasis;
        const requiredProductive = producedQty * perOutput;
        const waste = num(coveringWastage[r.rm_code]);
        const available = coveringV2Avail[r.rm_code] != null ? coveringV2Avail[r.rm_code] : num(r.floor_balance);
        return { ...r, requiredProductive, waste, available, required: requiredProductive + waste };
      });
      for (const r of preview) {
        if (r.required - num(r.available) > 0.0001) {
          return toast.error(`${r.rm_code}: needs ${fmt(r.required)} ${r.uom || "kg"} (productive ${fmt(r.requiredProductive)} + wastage ${fmt(r.waste)}), only ${fmt(r.available)} on Covering floor`);
        }
      }
      const wastageLines = preview
        .filter(r => r.waste > 0)
        .map(r => ({ rm_code: r.rm_code, wastage_qty: r.waste, reason: wastageReason || "" }));
      try {
        await api.post("/cwo-production-entries", {
          cwo_id: cwoId,
          produced_qty: producedQty,
          wastage_lines: wastageLines,
          entry_date: entryDate, shift, operator_id: operatorId, remarks,
          approved_by: approvedBy, approval_reason: approvalReason,
        });
        toast.success(`${pickedCwo?.cwo_no} · ${fmt(producedQty)} kg SFC · L2 BOM auto-consumed`);
        setCoveringProducedQty(""); setCoveringWastage({});
        setLineQtys({}); setLineWastage({}); setRemarks("");
        setWastageQty(""); setWastageReason(""); setApprovedBy(""); setApprovalReason("");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
      return;
    }

    const rows = rmRows
      .map(r => ({ ...r, qty: num(lineQtys[r.rm_code]), wastage: num(lineWastage[r.rm_code]) }))
      .filter(r => r.qty > 0 || r.wastage > 0);
    if (rows.length === 0) return toast.error("Enter a produced qty on at least one line");
    for (const r of rows) {
      const totalDraw = r.qty + r.wastage;
      if (dept === "SPANDEX_WARPING") {
        // CH-32 RC2 · V2-authoritative input-SFC availability (same authority the
        // backend enforces at posting). DISPLAY = PREVIEW = SAVE = V2 posting.
        // DECISION-01 · Partial-Upstream WIP Release · production eligibility and
        // the MIN(remaining, available − wastage) feasibility gate.
        if (spandexInfo && !spandexInfo.eligible) {
          return toast.error(`Not production eligible — ${spandexInfo.eligibility_reason}`);
        }
        const avail = num(spandexInfo?.available);
        if (spandexInfo && r.qty - num(spandexInfo.cwo_remaining) > 0.0001) {
          return toast.error(`Produced ${fmt(r.qty)} exceeds CWO remaining ${fmt(spandexInfo.cwo_remaining)} ${r.uom || "kg"}`);
        }
        const maxOut = Math.max(0, avail - r.wastage);
        if (spandexInfo && r.qty - maxOut > 0.0001) {
          return toast.error(`Produced ${fmt(r.qty)} exceeds max feasible ${fmt(Math.min(num(spandexInfo.cwo_remaining), maxOut))} ${r.uom || "kg"} (SFC available ${fmt(avail)} − wastage ${fmt(r.wastage)})`);
        }
        if (spandexInfo && totalDraw > avail + 0.0001) {
          return toast.error(`Input SFC ${spandexInfo.input_sfc_code}: only ${fmt(avail)} ${r.uom || "kg"} available (batch ${spandexInfo.input_batch}) — Produced ${fmt(r.qty)} + Wastage ${fmt(r.wastage)} = ${fmt(totalDraw)} exceeds it`);
        }
      } else if (totalDraw > r.floor_balance + 0.0001) {
        return toast.error(`${r.rm_code}: only ${fmt(r.floor_balance)} ${r.uom || "kg"} received at ${(DEPTS.find(d => d.code === dept) || {}).label} floor — Produced ${fmt(r.qty)} + Wastage ${fmt(r.wastage)} = ${fmt(totalDraw)} exceeds it`);
      }
      if (r.qty > r.balance + 0.0001) {
        return toast.error(`${r.rm_code}: only ${fmt(r.balance)} ${r.uom || ""} pending`);
      }
    }
    const isSpandexWarping = dept === "SPANDEX_WARPING";
    try {
      for (const r of rows) {
        if (isSpandexWarping) {
          // P17-UAT-13B REV-A · CH-32 RC4 · Post through the CANONICAL entry
          // (backend dispatches to spandex_warping_entry_create on stage_code=
          // SPANDEX_WARPING). The legacy /reverse-consume path is retired
          // (Phase 3J.17) and no longer used.
          await api.post("/cwo-production-entries", {
            cwo_id: cwoId, produced_qty: r.qty,
            entry_date: entryDate, shift, operator_id: operatorId, remarks,
            wastage_qty: r.wastage, wastage_reason: wastageReason,
            approved_by: approvedBy, approval_reason: approvalReason,
          });
        } else {
          await api.post("/cwo-production-entries", {
            cwo_id: cwoId, rm_code: r.rm_code, produced_qty: r.qty,
            entry_date: entryDate, shift, operator_id: operatorId, remarks,
            wastage_qty: r.wastage, wastage_reason: wastageReason,
            approved_by: approvedBy, approval_reason: approvalReason,
          });
        }
      }
      toast.success(
        isSpandexWarping
          ? `Recorded ${rows.length} Spandex Warping line(s) — SFC → SFSW 1:1 posted for ${pickedCwo?.cwo_no}`
          : `Recorded ${rows.length} line(s) on ${pickedCwo?.cwo_no}`
      );
      setLineQtys({}); setLineWastage({}); setRemarks("");
      setWastageQty(""); setWastageReason(""); setApprovedBy(""); setApprovalReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Reverse this production entry? The Child WO qty_produced will roll back.")) return;
    try {
      await api.delete(`/cwo-production-entries/${id}`);
      toast.success("Entry reversed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const todaysEntries = useMemo(
    () => entries.filter(e => e.entry_date === entryDate),
    [entries, entryDate],
  );
  const todaysTotal = todaysEntries.reduce((s, e) => s + num(e.produced_qty), 0);
  // Feb 2026 · Sortable table headers — DESC-on-`created_at` baseline
  // (Latest-on-Top) with click-to-flip on every column.
  const dpeSort = useSortableRows(todaysEntries, "created_at", "desc", { storageKey: "department-production-entry" });

  const active = DEPTS.find(d => d.code === dept) || DEPTS[0];

  return (
    <div className="p-6 space-y-5" data-testid="dept-prod-entry">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><FactoryIcon size={11} weight="bold" />Production Entry · {lockedDept ? PROCESS_TITLE[lockedDept] : "Department Production Entry"}</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">{lockedDept ? PROCESS_TITLE[lockedDept] : "Department Production Entry"}</h1>
          <div className="text-xs text-zinc-500 mt-1">{dept === "FINISHING" ? "Select a Work Order to load its production Item and reference Order, pick the Finishing machine, then record produced qty." : "Shop-floor entry · pick a Sales Order and Child WO, record today's produced qty per line."}</div>
        </div>
        <button onClick={refresh} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="dpe-refresh">
          <ArrowsClockwiseIcon size={14} weight="bold" className={loading ? "animate-spin" : ""} />{loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Big department tabs — hidden on a dedicated/locked process screen */}
      {!lockedDept && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="dpe-tabs">
        {DEPTS.map(d => {
          const on = d.code === dept;
          const stageCount = cwos.filter(c => c.stage_code === d.code && ENTERABLE.has(c.status)).length;
          return (
            <button
              key={d.code}
              data-testid={`dpe-tab-${d.code}`}
              onClick={() => changeDept(d.code)}
              className={`p-4 rounded-sm border text-left transition-colors ${on ? "bg-[var(--bg-2)] border-2" : "bg-[var(--bg-1)]/60 border-[var(--border)] hover:border-zinc-500"}`}
              style={on ? { borderColor: d.color } : {}}
            >
              <div className="flex items-center justify-between">
                <div className="text-lg font-black tracking-tight text-white">{d.label}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border" style={{ borderColor: d.color, color: d.color }}>
                  {stageCount} open
                </div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{d.code}</div>
            </button>
          );
        })}
      </div>
      )}

      {/* Entry form — kept intentionally simple for operator use */}
      <div className="tactical-card p-5 space-y-4" data-testid="dpe-form">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2">
            <FactoryIcon size={12} weight="bold" style={{ color: active.color }} />{
              dept === "WARPING" ? "Warping Production Entry" :
              dept === "COVERING" ? "Covering Production Entry" :
              dept === "SPANDEX_WARPING" ? "Spandex Warping Production Entry" :
              `Recording ${active.label} Production`
            }
          </div>
          {blockedCount > 0 && (
            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-amber-500/40 bg-amber-500/5 text-amber-300" data-testid="dpe-blocked-hint">
              {blockedCount} Child WO{blockedCount > 1 ? "s" : ""} blocked · awaiting deps
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Date">
            <input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)}
              className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
              data-testid="dpe-date" />
          </Field>
          <Field label="Shift">
            <div className="flex gap-1" data-testid="dpe-shifts">
              {["A", "B", "C"].map(s => (
                <button key={s} onClick={() => setShift(s)}
                  className={`flex-1 h-11 rounded-sm border text-sm font-mono uppercase tracking-widest ${shift === s ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}
                  data-testid={`dpe-shift-${s}`}>Shift {s}</button>
              ))}
            </div>
          </Field>
          <Field label="Operator">
            <select value={operatorId} onChange={e => setOperatorId(e.target.value)}
              className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
              data-testid="dpe-operator">
              <option value="">— Select operator —</option>
              {operators.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Remarks">
            <input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Optional notes"
              className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
              data-testid="dpe-remarks" />
          </Field>
        </div>

        {dept !== "FINISHING" && WO_PRIMARY.has(dept) && (
          <div className="space-y-3" data-testid="dpe-wo-primary-block">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Work Order *">
                <select value={woId} onChange={e => changeWo(e.target.value)}
                  className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white font-mono focus:outline-none focus:border-[var(--accent)]"
                  data-testid="dpe-wo">
                  <option value="">{woOptions.length ? "— Select Work Order —" : (blockedCount ? "All CWOs blocked" : "No eligible Work Orders at this stage")}</option>
  {woOptions.map(o => <option key={o.id} value={o.id}>{o.no}{o.fgLabel ? ` | ${o.fgLabel}` : ""}</option>)}
                </select>
              </Field>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">SO / ESO Reference</div>
                <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-zinc-300" data-testid="dpe-wo-soref">
                  {selectedWo
                    ? `${woSourceTypeLabel(selectedWo.source_type)} · ${selectedWo.source_no || "—"}${headerFg ? ` | ${headerFg}` : ""}`
                    : "—"}
                </div>
              </div>
            </div>
            {woId && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label={cwoOptions.length > 1 ? "Child Work Order *" : "Child Work Order"}>
                  {cwoOptions.length === 0 ? (
                    <div className="h-11 px-3 border border-red-500/40 bg-red-500/5 rounded-sm flex items-center font-mono text-red-300 text-xs" data-testid="dpe-cwo-none">
                      No eligible {active.label} Child Work Order is available for the selected Work Order.
                    </div>
                  ) : cwoOptions.length === 1 ? (
                    <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-emerald-300" data-testid="dpe-cwo-resolved">
                      {cwoOptions[0].cwo_no} · {fmt(cwoOptions[0].qty_planned)} {cwoOptions[0].uom} · {cwoOptions[0].status}
                    </div>
                  ) : (
                    <select value={cwoId} onChange={e => { setCwoId(e.target.value); setLineQtys({}); setCoveringProducedQty(""); }}
                      className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white font-mono focus:outline-none focus:border-[var(--accent)]"
                      data-testid="dpe-cwo">
                      <option value="">— Select Child WO —</option>
                      {cwoOptions.map(c => <option key={c.id} value={c.id}>{c.cwo_no} · {fmt(c.qty_planned)} {c.uom} · {c.status}</option>)}
                    </select>
                  )}
                </Field>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Product / Item</div>
                  <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-zinc-300" data-testid="dpe-wo-product">
                    {headerFg || "—"}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {dept !== "FINISHING" && !WO_PRIMARY.has(dept) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="SO / ESO">
            <select value={soId} onChange={e => { setSoId(e.target.value); setCwoId(""); setLineQtys({}); }}
              className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
              data-testid="dpe-so">
              <option value="">{soOptions.length ? "— Select SO / ESO —" : (blockedCount ? "All CWOs blocked" : "No CWOs at this stage")}</option>
              {soOptions.map(o => <option key={o.id} value={o.id}>{o.type} · {o.no} — {o.customer}</option>)}
            </select>
          </Field>
          <Field label="Child WO">
            <select value={cwoId} onChange={e => { setCwoId(e.target.value); setLineQtys({}); }}
              className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
              data-testid="dpe-cwo">
              <option value="">{cwoOptions.length ? "— Select Child WO —" : "Pick SO / ESO first"}</option>
              {cwoOptions.map(c => <option key={c.id} value={c.id}>{c.cwo_no} · {fmt(c.qty_planned)} {c.uom} · {c.status}</option>)}
            </select>
          </Field>
        </div>
        )}

        {/* P17-UAT-16B-1 · FINISHING · WO-first authority block */}
        {dept === "FINISHING" && (
          <div className="space-y-3" data-testid="dpe-finishing-block">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Work Order *">
                <select value={finWoId} onChange={e => { setFinWoId(e.target.value); setFinItemId(""); }}
                  className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
                  data-testid="dpe-fin-wo">
                  <option value="">{finWorkOrders.length ? "— Select Work Order —" : "No eligible Work Orders"}</option>
                  {finWorkOrders.map(w => (
                    <option key={w.id} value={w.id}>{w.po_no || "WO"} · {w.product_code || w.item_code || "—"} · {(w.source_type || "SO").toUpperCase()} {w.source_order_no || ""} · {w.status}</option>
                  ))}
                </select>
              </Field>
              <Field label="Finishing Machine *">
                <select value={finMachineId} onChange={e => setFinMachineId(e.target.value)}
                  className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
                  data-testid="dpe-fin-machine">
                  <option value="">{finMachines.length ? "— Select Machine —" : "No active Finishing machines in Machine Master"}</option>
                  {finMachines.map(m => <option key={m.id} value={m.id}>{m.code} · {m.name}</option>)}
                </select>
              </Field>
            </div>
            {finWoId && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dpe-fin-derived">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">SO / ESO Reference</div>
                  <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-zinc-300" data-testid="dpe-fin-soref">{finSoRef || "—"}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">WO Item / Product {finItems.length > 1 ? "*" : ""}</div>
                  {finItems.length === 0 ? (
                    <div className="h-11 px-3 border border-red-500/40 bg-red-500/5 rounded-sm flex items-center font-mono text-red-300 text-xs" data-testid="dpe-fin-noitem">No valid production item is available for the selected Work Order.</div>
                  ) : finItems.length === 1 ? (
                    <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-emerald-300" data-testid="dpe-fin-product">{finProduct || "—"}</div>
                  ) : (
                    <select value={finItemId} onChange={e => setFinItemId(e.target.value)}
                      className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
                      data-testid="dpe-fin-item">
                      <option value="">— Select WO Item —</option>
                      {finItems.map(li => <option key={li.item_id || li.product_code} value={li.item_id || li.product_code}>{li.product_code || li.item_code} {li.description ? `· ${li.description}` : ""}</option>)}
                    </select>
                  )}
                </div>
              </div>
            )}
            {finWoId && finItem && (
              <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-0)]/40 p-3 space-y-3" data-testid="dpe-fin-qtybox">
                {!finBomOk ? (
                  <div className="text-xs text-red-300 font-mono px-1 py-2" data-testid="dpe-fin-nobom">
                    {finBomLoading ? "Resolving approved BOM weight…" : "No valid approved BOM weight is available for the selected Work Order Item/Product. Save is blocked."}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field label="Entry Basis *">
                        <select value={finBasis} onChange={e => { setFinBasis(e.target.value); setFinPrimary(""); }}
                          className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
                          data-testid="dpe-fin-basis">
                          <option value="WEIGHT">Weight (kg)</option>
                          <option value="LENGTH">Length (m)</option>
                        </select>
                      </Field>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">BOM Standard Weight</div>
                        <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center justify-between font-mono text-amber-300" data-testid="dpe-fin-bomgpm">
                          <span>{fmt(finGpm, 3)} g/m</span>
                          {finBom?.bom_version != null && <span className="text-[10px] text-zinc-500">v{finBom.bom_version}</span>}
                        </div>
                      </div>
                      <div />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {finBasis === "WEIGHT" ? (
                        <>
                          <Field label="Production Weight (kg) *">
                            <input type="number" min="0" step="0.001" value={finPrimary} onChange={e => setFinPrimary(e.target.value)} placeholder="0.000"
                              className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-right font-mono focus:outline-none focus:border-[var(--accent)]"
                              data-testid="dpe-fin-kg" />
                          </Field>
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Calculated Length (m)</div>
                            <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center justify-end font-mono text-emerald-300" data-testid="dpe-fin-calc-m">{fmt(finCalcM, 3)}</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <Field label="Production Length (m) *">
                            <input type="number" min="0" step="0.001" value={finPrimary} onChange={e => setFinPrimary(e.target.value)} placeholder="0.000"
                              className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-right font-mono focus:outline-none focus:border-[var(--accent)]"
                              data-testid="dpe-fin-m" />
                          </Field>
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Calculated Weight (kg)</div>
                            <div className="h-11 px-3 border border-[var(--border)] rounded-sm flex items-center justify-end font-mono text-emerald-300" data-testid="dpe-fin-calc-kg">{fmt(finCalcKg, 3)}</div>
                          </div>
                        </>
                      )}
                    </div>
                    {/* 16B-3 · Source Woven-SFG + process loss + auto-resolved destination */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dpe-fin-stock">
                      <Field label="Source Woven-SFG Batch *">
                        <select value={finSourceKey} onChange={e => setFinSourceKey(e.target.value)}
                          className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white focus:outline-none focus:border-[var(--accent)]"
                          data-testid="dpe-fin-source">
                          <option value="">{finSources.length ? "— Select source batch —" : "No eligible Woven-SFG for this WO"}</option>
                          {finSources.map(s => (
                            <option key={`${s.batch_lot}||${s.godown_id}`} value={`${s.batch_lot}||${s.godown_id}`}>
                              {s.batch_lot} · {s.godown_code} · {fmt(s.available_kg, 4)} kg
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Process Loss / Wastage (kg)">
                        <input type="number" min="0" step="0.001" value={finLoss} onChange={e => setFinLoss(e.target.value)} placeholder="0.000"
                          className="w-full h-11 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-right font-mono focus:outline-none focus:border-[var(--accent)]"
                          data-testid="dpe-fin-loss" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dpe-fin-conservation">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Input kg</div>
                        <div className="h-10 px-3 border border-[var(--border)] rounded-sm flex items-center justify-end font-mono text-white" data-testid="dpe-fin-input-kg">{fmt(finInputKg, 3)}</div>
                        <div className="text-[10px] text-right font-mono text-zinc-500 mt-0.5" data-testid="dpe-fin-input-m">{finGpm > 0 ? `${fmt(finInputM, 3)} m` : "— m"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Output kg</div>
                        <div className="h-10 px-3 border border-[var(--border)] rounded-sm flex items-center justify-end font-mono text-emerald-300" data-testid="dpe-fin-output-kg">{fmt(finCalcKg, 3)}</div>
                        <div className="text-[10px] text-right font-mono text-zinc-500 mt-0.5" data-testid="dpe-fin-output-m">{finGpm > 0 ? `${fmt(finCalcM, 3)} m` : "— m"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Process Loss kg</div>
                        <div className="h-10 px-3 border border-[var(--border)] rounded-sm flex items-center justify-end font-mono text-amber-300" data-testid="dpe-fin-loss-kg">{fmt(finLossNum, 3)}</div>
                        <div className="text-[10px] text-right font-mono text-zinc-500 mt-0.5" data-testid="dpe-fin-loss-m">{finGpm > 0 ? `${fmt(finLossM, 3)} m` : "— m"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                          Destination {finNext ? `· next: ${finNext.replace(/_/g, " ")}` : ""}
                        </div>
                        {finDestOptions.length > 1 ? (
                          <select value={finDestId} onChange={e => setFinDestId(e.target.value)}
                            className="w-full h-10 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sky-300 text-xs font-mono focus:outline-none focus:border-[var(--accent)]"
                            data-testid="dpe-fin-dest-select">
                            <option value="">— Select Packing WIP —</option>
                            {finDestOptions.map(o => (
                              <option key={o.godown_id} value={o.godown_id}>{o.godown_code} · {o.godown_name}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="h-10 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-sky-300 text-xs" data-testid="dpe-fin-dest">
                            {finDestOptions.length === 1 ? `${finDestOptions[0].godown_code} · ${finDestOptions[0].godown_name}` : (finDestReason || "No valid destination")}
                          </div>
                        )}
                      </div>
                    </div>
                    {finSource && (
                      <div className="text-[11px] font-mono text-zinc-400 px-1" data-testid="dpe-fin-genealogy">
                        Output batch → <span className="text-zinc-200">{finOutBatchPreview}</span> · available {fmt(finSourceAvail, 4)} kg
                        {!finStockOk && finInputKg > 0 && <span className="text-red-400"> · insufficient for {fmt(finInputKg, 3)} kg input</span>}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => { finPostRef.current = false; submit(); }} disabled={!finWoId || !finItem || !finMachineId || !finBomOk || num(finPrimary) <= 0 || finPosting}
                className="h-12 px-6 rounded-sm border border-[var(--border)] hover:border-[var(--accent)] disabled:opacity-40 text-white text-base font-semibold flex items-center gap-2"
                data-testid="dpe-fin-submit">
                <CheckCircleIcon size={16} weight="bold" />Save Finishing Production
              </button>
              <button onClick={() => { finPostRef.current = true; submit(); }}
                disabled={!finWoId || !finItem || !finMachineId || !finBomOk || num(finPrimary) <= 0 || !finStockOk || !finDestId || finPosting}
                className="h-12 px-6 rounded-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-base font-semibold flex items-center gap-2"
                data-testid="dpe-fin-submit-post">
                <CheckCircleIcon size={16} weight="bold" />{finPosting ? "Posting…" : "Save & Post Finishing Stock"}
              </button>
            </div>
          </div>
        )}

        {/* Sep 2026 · Phase 3J.17 · Covering output-driven UI ─────────
            When dept === COVERING, replace the RM-per-line grid with:
              • Output SF (SFC) product card
              • Read-only Level 2 BOM auto-consumption table
              • ONE editable SFC Produced Qty field
            Operator only enters the SFC output; backend re-derives
            L2 BOM productive consumption authoritatively via
            covering_output_entry_create(). */}
        {pickedCwo && dept === "COVERING" && (() => {
          const outputSfcCode =
            pickedCwo.output_sfc_code ||
            (pickedCwo.items || []).find(it => it.output_sfc_code)?.output_sfc_code || "";
          const outputSfcId =
            pickedCwo.output_sfc_id ||
            (pickedCwo.items || []).find(it => it.output_sfc_id)?.output_sfc_id || "";
          const l2BomId = pickedCwo.l2_bom_id || (pickedCwo.items || []).find(it => it.l2_bom_id)?.l2_bom_id || "";
          const configured = !!outputSfcCode && !!outputSfcId;
          const bomBasis = num(pickedCwo.qty_planned) || 1;
          const plannedOutput = num(pickedCwo.qty_planned);
          const previouslyProduced = num(pickedCwo.qty_produced);
          const cwoBalance = Math.max(0, plannedOutput - previouslyProduced);
          const producedQty = num(coveringProducedQty);
          // Preview per L2 line. Two DISTINCT concepts (P17-UAT-13A REV-C §10):
          //  • cumulativeConsumed — already-posted productive consumption for
          //    this RM (from the CWO item roll-up); shown regardless of input.
          //  • requiredProductive — the NEW proposed entry's consumption
          //    (producedQty × ratio); only meaningful while typing a qty.
          // `floor_balance` is the authoritative CURRENT V2 availability, which
          // ALREADY reflects historical consumption — so Balance After only
          // deducts the NEW proposed draw (never double-counts history).
          const preview = (rmRows || []).map(r => {
            const perOutput = num(r.qty) / bomBasis;
            const cumulativeConsumed = num(r.produced);
            // V2 authority governs availability (legacy floor_balance is stale
            // for V2-only Covering consumption). Fall back only while loading.
            const available = coveringV2Avail[r.rm_code] != null ? coveringV2Avail[r.rm_code] : num(r.floor_balance);
            const requiredProductive = producedQty * perOutput;
            const waste = num(coveringWastage[r.rm_code]);
            const totalReq = requiredProductive + waste;
            const balanceAfter = producedQty > 0 ? (available - totalReq) : available;
            const sufficient = balanceAfter >= -0.0001;
            return { ...r, perOutput, cumulativeConsumed, available, requiredProductive, waste, totalReq, balanceAfter, sufficient };
          });
          const anyInsufficient = preview.some(r => !r.sufficient);
          const totalProductive = preview.reduce((s, r) => s + r.requiredProductive, 0);
          const totalWastage = preview.reduce((s, r) => s + r.waste, 0);
          const totalPhysical = totalProductive + totalWastage;
          return (
            <div className="space-y-4" data-testid="dpe-covering-block">
              {/* — OUTPUT SF (SFC) PRODUCT — */}
              <div className="border border-pink-500/40 bg-pink-500/5 rounded-sm p-4" data-testid="dpe-covering-output-sf">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-pink-300">Output SF (SFC) Product</div>
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-pink-500/40 bg-pink-500/10 text-pink-300" data-testid="dpe-covering-mode-badge">
                    L2 BOM Auto Consumption
                  </span>
                </div>
                {configured ? (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">SF Code</div>
                      <div className="font-mono text-white text-base break-all leading-tight" data-testid="dpe-covering-sf-code">{outputSfcCode}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Planned Qty</div>
                      <div className="font-mono tabular-nums text-white" data-testid="dpe-covering-planned">{fmt(plannedOutput)} kg</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Produced</div>
                      <div className="font-mono tabular-nums text-emerald-300" data-testid="dpe-covering-prev-produced">{fmt(previouslyProduced)} kg</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Balance</div>
                      <div className="font-mono tabular-nums text-amber-300" data-testid="dpe-covering-balance">{fmt(cwoBalance)} kg</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Status</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">{pickedCwo.status || "READY"}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-red-300 font-mono" data-testid="dpe-covering-not-configured">
                    ⚠ COVERING OUTPUT SF NOT CONFIGURED — re-run Explode BOM · Generate Child WOs to stamp `output_sfc_code / output_sfc_id / l2_bom_id`.
                  </div>
                )}
              </div>

              {/* — LEVEL 2 BOM · RAW MATERIAL CONSUMPTION (READ-ONLY) — */}
              {configured && (
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto" data-testid="dpe-covering-l2-table">
                  <div className="px-3 py-2 bg-[var(--bg-2)]/60 text-[10px] font-mono uppercase tracking-widest text-zinc-400 flex items-center justify-between">
                    <span>Level 2 BOM · Auto-Consumption Preview {l2BomId ? `· ${l2BomId.slice(0,8)}` : ""}</span>
                    <span className="text-zinc-500">Read-only · backend re-derives from BOM at post</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="text-left px-3 py-2 w-10">#</th>
                        <th className="text-left px-3 py-2">RM Code</th>
                        <th className="text-left px-3 py-2">Description</th>
                        <th className="text-right px-3 py-2">BOM Qty</th>
                        <th className="text-right px-3 py-2">Ratio</th>
                        <th className="text-right px-3 py-2" title="Cumulative productive consumption already posted for this CWO">Consumed ∑</th>
                        <th className="text-right px-3 py-2" title="Consumption for the NEW production qty being entered">New Entry</th>
                        <th className="text-right px-3 py-2">Wastage</th>
                        <th className="text-right px-3 py-2" title="Current authoritative V2 availability on the Covering floor">Available</th>
                        <th className="text-right px-3 py-2" title="Available minus the NEW proposed draw">Balance After</th>
                        <th className="text-center px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.length === 0 && (
                        <tr><td colSpan={11} className="text-center py-6 text-zinc-500 italic">L2 BOM has no lines · check Explode BOM</td></tr>
                      )}
                      {preview.map((r, i) => (
                        <tr key={r.rm_code || i} className="border-b border-[var(--border)]" data-testid={`dpe-covering-l2-row-${r.rm_code}`}>
                          <td className="px-3 py-2 text-zinc-500 text-xs">{i + 1}</td>
                          <td className="px-3 py-2 font-mono text-white break-all leading-tight max-w-[20rem]">{r.rm_code}</td>
                          <td className="px-3 py-2 text-zinc-400">{r.description || r.rm_name || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmt(r.qty)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400 text-xs">{r.perOutput.toFixed(4)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300" data-testid={`dpe-covering-cum-${r.rm_code}`}>{fmt(r.cumulativeConsumed)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300" data-testid={`dpe-covering-consume-${r.rm_code}`}>{producedQty > 0 ? fmt(r.requiredProductive) : "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <input type="number" min="0" step="0.001" value={coveringWastage[r.rm_code] || ""}
                              onChange={e => setCoveringWastage(prev => ({ ...prev, [r.rm_code]: e.target.value }))}
                              className="w-24 h-8 px-2 bg-[var(--bg-0)] border border-amber-500/30 rounded-sm text-amber-200 text-right font-mono text-xs focus:outline-none focus:border-amber-500"
                              data-testid={`dpe-covering-waste-${r.rm_code}`} placeholder="0" />
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-sky-200" data-testid={`dpe-covering-avail-${r.rm_code}`}>{fmt(r.available)}</td>
                          <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.balanceAfter < 0 ? "text-red-400" : "text-emerald-200"}`} data-testid={`dpe-covering-balance-after-${r.rm_code}`}>{fmt(r.balanceAfter)}</td>
                          <td className="px-3 py-2 text-center">
                            {producedQty > 0 ? (
                              r.sufficient
                                ? <span className="text-[10px] font-mono uppercase text-emerald-300" data-testid={`dpe-covering-status-${r.rm_code}`}>OK</span>
                                : <span className="text-[10px] font-mono uppercase text-red-400 font-bold" data-testid={`dpe-covering-status-${r.rm_code}`}>Insufficient</span>
                            ) : (
                              <span className="text-[10px] font-mono uppercase text-zinc-600">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* — PRODUCTION QUANTITY (SFC) — */}
              {configured && (
                <div className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-4" data-testid="dpe-covering-production-input">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-3">Production Quantity (SFC)</div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <label className="col-span-2 md:col-span-1">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">SFC Produced Qty *</div>
                      <input type="number" min="0" step="0.001" value={coveringProducedQty}
                        onChange={e => setCoveringProducedQty(e.target.value)}
                        placeholder="0.000"
                        className="w-full h-12 px-3 bg-[var(--bg-0)] border border-cyan-500/40 rounded-sm text-cyan-200 text-right font-mono text-lg focus:outline-none focus:border-cyan-500"
                        data-testid="dpe-covering-produced-qty" />
                    </label>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Total Productive Consumption</div>
                      <div className="h-12 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-red-200 tabular-nums" data-testid="dpe-covering-total-productive">
                        {producedQty > 0 ? `${fmt(totalProductive)} kg` : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Total Wastage</div>
                      <div className="h-12 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-amber-300 tabular-nums" data-testid="dpe-covering-total-wastage">
                        {totalWastage > 0 ? `${fmt(totalWastage)} kg` : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Total Physical Reduction</div>
                      <div className="h-12 px-3 border border-[var(--border)] rounded-sm flex items-center font-mono text-white tabular-nums" data-testid="dpe-covering-total-physical">
                        {totalPhysical > 0 ? `${fmt(totalPhysical)} kg` : "—"}
                      </div>
                    </div>
                  </div>
                  {producedQty > cwoBalance && (
                    <div className="mt-3 text-xs font-mono text-red-300" data-testid="dpe-covering-overrun">
                      ⚠ Produced Qty {fmt(producedQty)} exceeds CWO balance {fmt(cwoBalance)} kg
                    </div>
                  )}
                  {anyInsufficient && producedQty > 0 && (
                    <div className="mt-3 text-xs font-mono text-red-300" data-testid="dpe-covering-insufficient">
                      ⚠ One or more L2 BOM inputs are insufficient on Covering floor · POST blocked
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* P17-UAT-13B REV-A · CH-32 RC3 · Spandex Warping INPUT SFC → OUTPUT SFSW
            identity banner. Codes/batches come straight from the authoritative
            CWO/backend (never constructed on the client). */}
        {pickedCwo && dept === "SPANDEX_WARPING" && spandexInfo && (
          <div className="border border-amber-500/30 rounded-sm bg-amber-500/5 p-4" data-testid="dpe-spandex-identity">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300/80 mb-3">Spandex Warping · 1:1 Conversion Identity</div>
            <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
              <div className="max-w-[26rem]">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Input SFC</div>
                <div className="font-mono text-sm text-sky-200 break-all leading-tight" data-testid="dpe-spandex-input-sfc">{spandexInfo.input_sfc_code || "—"}</div>
                <div className="text-[11px] font-mono text-zinc-500">Batch: {spandexInfo.input_batch || "—"}</div>
              </div>
              <div className="text-amber-300 text-lg font-mono mt-3">→</div>
              <div className="max-w-[26rem]">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Output SFSW</div>
                <div className="font-mono text-sm text-emerald-200 break-all leading-tight" data-testid="dpe-spandex-output-sfsw">{spandexInfo.output_sfsw_code || "—"}</div>
                <div className="text-[11px] font-mono text-zinc-500">Batch: {spandexInfo.output_batch || "—"}</div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Input SFC Available (V2)</div>
                <div className="font-mono text-sm text-sky-200" data-testid="dpe-spandex-available">{fmt(spandexInfo.available)} {pickedCwo.uom || "kg"}</div>
                <div className="text-[11px] font-mono text-zinc-500">Remaining: {fmt(spandexInfo.cwo_remaining)} · Max Feasible: <span className="text-amber-200" data-testid="dpe-spandex-max-feasible">{fmt(spandexInfo.max_feasible_qty)}</span> {pickedCwo.uom || "kg"}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-amber-500/20 flex items-center gap-2" data-testid="dpe-spandex-eligibility">
              {spandexInfo.eligible ? (
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="dpe-spandex-eligible">Production Eligible</span>
              ) : (
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm bg-red-500/15 text-red-300 border border-red-500/30" data-testid="dpe-spandex-not-eligible">Not Eligible</span>
              )}
              <span className="text-[11px] font-mono text-zinc-400">{spandexInfo.eligibility_reason}</span>
              {spandexInfo.cwo_status && spandexInfo.cwo_status.toLowerCase() !== "ready" && (
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">Upstream state: {spandexInfo.cwo_status}</span>
              )}
            </div>
          </div>
        )}

        {/* Per-line output grid — Warping / Spandex Warping · retained · unchanged */}
        {pickedCwo && dept !== "COVERING" && rmRows.length > 0 && (
          <div className="border border-[var(--border)] rounded-sm overflow-x-auto" data-testid="dpe-line-grid">
            <div className="px-3 py-2 bg-[var(--bg-2)]/60 text-[10px] font-mono uppercase tracking-widest text-zinc-400 flex items-center justify-between">
              <span>{pickedCwo.cwo_no} · Enter today's output per line</span>
              {dept === "WARPING" && (
                <span
                  data-testid="dpe-mode-badge"
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-cyan-500/40 bg-cyan-500/5 text-cyan-300"
                  title="Warping: enter SFW output; RM consumes 1:1 (no BOM). Wastage remains additive."
                >
                  1:1 RM Conversion
                </span>
              )}
              {dept === "COVERING" && (
                <span
                  data-testid="dpe-mode-badge"
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-pink-500/40 bg-pink-500/5 text-pink-300"
                  title="Covering: enter SFC output; Level 2 BOM inputs consume proportionately. Wastage remains separate."
                >
                  L2 BOM Auto Consumption
                </span>
              )}
              {dept === "SPANDEX_WARPING" && (
                <span
                  data-testid="dpe-mode-badge"
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/5 text-amber-300"
                  title="Spandex Warping: enter SFSW output; SFC consumes 1:1 (no BOM). Ends define SFSW identity only."
                >
                  1:1 SFC Conversion
                </span>
              )}
            </div>
            <table className="w-full min-w-[1180px] text-sm" data-testid="dpe-line-grid-table">
              <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-2 w-10">#</th>
                  {dept === "WARPING" ? (
                    /* Phase 3J.9C · replace legacy "SF Code" (which incorrectly
                       showed the source RM) with an explicit INPUT RM → ENDS →
                       OUTPUT SFW column set driven by the Phase 3J.9A CWO
                       conversion contract. Spec §3, §4, §34.
                       Phase 1B.1-UX · min-w stops the composite cell from
                       being crushed to 1 char wide when the viewport is
                       narrower than the sum of numeric columns; parent
                       div.overflow-x-auto delivers controlled horizontal
                       scroll instead of character-level breaking. */
                    <th className="text-left px-3 py-2 min-w-[22rem]" title="INPUT RM (auto-loaded from Warping CWO) · Ends · OUTPUT SFW">
                      Input RM · Ends → Output SFW
                    </th>
                  ) : (
                    <th className="text-left px-3 py-2 min-w-[16rem]">{dept === "SPANDEX_WARPING" || dept === "COVERING" ? "SF Code" : "RM / SF Code"}</th>
                  )}
                  <th className="text-right px-3 py-2 w-24" title="Planned qty per plan">Planned</th>
                  <th className="text-right px-3 py-2 w-24" title="Produced cumulative">Produced</th>
                  <th className="text-right px-3 py-2 w-24" title="Plan balance = Planned − Produced">Balance</th>
                  {dept !== "SPANDEX_WARPING" && (
                    <>
                      <th className="text-right px-3 py-2 w-24 border-l border-[var(--border)]" title="Received at this floor (RM Store / prev stage)">Received</th>
                      <th className="text-right px-3 py-2 w-24" title="Consumed at this floor so far">Consumed</th>
                      <th className="text-right px-3 py-2 w-28" title="Balance Available = Received − Consumed">Balance Avail</th>
                      <th className="text-right px-3 py-2 w-28" title="Item Waste Σ — cumulative wastage recorded against this RM Code across every Sales Order & stage">Item Waste ∑</th>
                    </>
                  )}
                  {dept === "WARPING" && (
                    /* Phase 3J.9C · SFW cumulative output for the exact CWO. */
                    <th className="text-right px-3 py-2 w-32" title="Cumulative SFW output posted against this CWO's exact conversion line">SFW Produced ∑</th>
                  )}
                  <th className="text-right px-3 py-2 w-36 border-l border-[var(--border)]">Produced Qty</th>
                  <th className="text-right px-3 py-2 w-32" title={dept === "SPANDEX_WARPING" ? "New-entry process wastage — additional SFC consumption on top of Produced Qty" : "Wastage from this line — added to Produced Qty for total floor consumption"}>Wastage Qty</th>
                  <th className="text-left px-3 py-2 w-16">UoM</th>
                </tr>
              </thead>
              <tbody>
                {rmRows.map((r, i) => {
                  const isSw = dept === "SPANDEX_WARPING";
                  const sfMissing = isSw && !r.sf_article_id;
                  const curWastage = num(lineWastage[r.rm_code]);
                  const curQty = num(lineQtys[r.rm_code]);
                  // Mar 2, 2026 · Total floor draw = Produced Qty + Wastage Qty.
                  // REV-B · DECISION-01 §4 · Spandex Warping feasibility is
                  // dynamic: SFC is a shared physical pool, so
                  //   max produced = MIN(CWO remaining, MAX(0, V2 available − wastage))
                  //   max wastage  = MAX(0, V2 available − produced)
                  // (backend re-validates against live V2). Non-SPX stages keep
                  // the received-floor draw.
                  const spxAvail = isSw ? num(spandexInfo?.available) : 0;
                  const maxProduced = isSw
                    ? Math.max(0, Math.min(r.balance, spxAvail - curWastage))
                    : Math.max(0, Math.min(r.balance, r.floor_balance - curWastage));
                  const maxWastage = isSw
                    ? Math.max(0, spxAvail - curQty)
                    : Math.max(0, r.floor_balance - curQty);
                  const noStock = !isSw && r.floor_balance <= 0;
                  return (
                    <tr key={`${r.sf_article_id || r.rm_id || "rm"}-${r.rm_code}-${i}`} className="border-t border-[var(--border)]/60" data-testid={`dpe-line-${r.rm_code}`}>
                      <td className="px-3 py-2 font-mono text-zinc-500">{i + 1}</td>
                      {dept === "WARPING" ? (
                        /* Phase 3J.9C · Explicit conversion contract cell.
                           Renders the Phase 3J.9A stable-ID trio: Input RM
                           (source of truth for MI + procurement), Ends (BOM
                           parameter), Output SFW (auto-resolved, read-only,
                           spec §9). Legacy entries where output_sfw_code is
                           empty carry a LEGACY chip so operators know the
                           historical row was not part of the canonical
                           conversion contract (spec §23).

                           Phase 3J.9C.2 · Row-level LEGACY / PRE-CANONICAL
                           badge for rows with production activity but no
                           canonical consumption posting — pulled OUT of
                           the numeric Consumed cell (spec §3, §4) so the
                           status never mixes with quantity values. */
                        <td className="px-3 py-2 min-w-[22rem]" data-testid={`dpe-warping-cell-${r.rm_code}`}>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-start gap-2">
                              <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 shrink-0 mt-0.5">INPUT RM</span>
                              <span className="font-mono text-[var(--accent)] break-words leading-tight max-w-[28rem]" title={r.input_rm_code} data-testid={`dpe-input-rm-${r.rm_code}`}>{r.input_rm_code || r.rm_code}</span>
                              {noStock && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300" title="Nothing received at Warping floor yet — issue material from RM Store first">Not Received</span>}
                              {r.consumption_status === "LEGACY_PRE_CANONICAL" && (
                                <span
                                  className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
                                  title="Historical production recorded before canonical RM consumption posting was enabled. Produced and wastage quantities are preserved, but authoritative productive consumption is unavailable."
                                  data-testid={`dpe-row-legacy-${r.rm_code}`}
                                >LEGACY · PRE-CANONICAL</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                              <span className="font-mono uppercase tracking-widest px-1 py-0.5 rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-300" data-testid={`dpe-ends-${r.rm_code}`}>
                                {r.no_of_ends > 0 ? `${r.no_of_ends} ends` : "— ends —"}
                              </span>
                              <span className="text-zinc-600">→ Warping →</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 shrink-0 mt-0.5">OUTPUT SFW</span>
                              {r.output_sfw_code ? (
                                <span className="font-mono text-cyan-300 break-words leading-tight max-w-[28rem]" title={r.output_sfw_code} data-testid={`dpe-output-sfw-${r.rm_code}`}>{r.output_sfw_code}</span>
                              ) : (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300" title="This CWO items[] does not yet carry a Phase 3J.9A stable-ID output_sfw_code — re-run Explode BOM + Generate Child Work Orders to backfill" data-testid={`dpe-output-sfw-legacy-${r.rm_code}`}>LEGACY · SFW NOT LINKED</span>
                              )}
                            </div>
                          </div>
                        </td>
                      ) : (
                        <td className="px-3 py-2 font-mono text-[var(--accent)] min-w-[16rem]">
                          <div className="flex items-start gap-2">
                            <span className="break-words leading-tight max-w-[28rem]">{r.rm_code}</span>
                            {r.kind === "SF" && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-300" title="Semi-Finished code — child BOM drives reverse consumption">SF</span>}
                            {sfMissing && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300" title="Re-run Explode BOM on the parent WO to re-link SF Article ID">No SF link</span>}
                            {noStock && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300" title={`Nothing received at ${(DEPTS.find(d => d.code === dept) || {}).label} floor yet — issue material from RM Store or the previous stage first`}>Not Received</span>}
                          </div>
                        </td>
                      )}
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmt(r.planned)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{fmt(r.produced)}</td>
                      <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.balance === 0 ? "text-emerald-400" : "text-amber-300"}`}>{fmt(r.balance)}</td>
                      {!isSw && (() => {
                        // Mar 3, 2026 · Live Consumed = historical + this row's (Produced + Wastage).
                        // Balance Avail reflects what's left AFTER this entry posts, so operators
                        // see the impact of Produced 100 + Wastage 5 → Consumed 105 immediately.
                        //
                        // Phase 3J.9C.1 · For WARPING, Consumed reads from
                        // authoritative posting data (productive_consumed_qty)
                        // instead of the combined godown-balances aggregation
                        // that mixes productive + wastage into one draw. Legacy
                        // pre-canonical entries (no PRODUCTION_CONSUMPTION
                        // posting for this cwo+rm) keep the historical 0 and
                        // surface a LEGACY chip per spec §11.
                        //
                        // Phase 3J.9C.2 · When the row is LEGACY_PRE_CANONICAL,
                        // Consumed AND Balance Avail render as "—" (spec §3,
                        // §6) so operators never confuse an unavailable
                        // authoritative posting with an actual zero
                        // consumption. Row-level LEGACY chip already appears
                        // in the composite Warping cell above — status is
                        // never mixed with the numeric column here.
                        const isWarping = dept === "WARPING";
                        const isLegacyRow = isWarping && r.consumption_status === "LEGACY_PRE_CANONICAL";
                        const priorConsumed = isWarping
                          ? Number(r.productive_consumed_qty || 0)
                          : Number(r.consumed_floor || 0);
                        const priorWastage = isWarping
                          ? Number(r.actual_wastage_qty_authoritative || 0)
                          : 0;
                        const liveConsumed = priorConsumed + curQty;
                        const liveBalance = Math.max(0, Number(r.received || 0) - (priorConsumed + priorWastage + curQty + curWastage));
                        const isDirty = curQty > 0 || curWastage > 0;
                        // Phase 3J.15D · Legacy display derivation (spec §4).
                        // For a LEGACY_PRE_CANONICAL row, canonical
                        // consumption fields are 0 but the historical
                        // production entries carry `produced_qty` +
                        // `wastage_qty`. We derive Consumed = produced
                        // and Balance = received - produced - wastage
                        // ONLY when Received > 0, Produced+Wastage > 0,
                        // and the arithmetic is consistent
                        // (received >= produced + wastage within a
                        // 0.001 tolerance). Any other shape falls back
                        // to n/a with the existing diagnostic tooltip.
                        // This is presentation-only — no V2 write,
                        // no canonical mutation.
                        const legacyProduced = Number(r.sfw_produced_to_date || 0);
                        const legacyWastage  = Number(r.legacy_wastage_total || 0);
                        const legacyReceived = Number(r.received || 0);
                        const derivableLegacy = isLegacyRow
                          && legacyReceived > 0
                          && (legacyProduced + legacyWastage) > 1e-6
                          && (legacyReceived + 1e-3) >= (legacyProduced + legacyWastage);
                        const derivedConsumed = legacyProduced;
                        const derivedBalance = Math.max(0,
                          Number((legacyReceived - legacyProduced - legacyWastage).toFixed(4)));
                        // Cumulative wastage ever recorded for this RM (all history)
                        // + today's wastage on this row, so operators see the running total.
                        const liveItemWaste = Number(r.item_waste_total || 0) + curWastage;
                        const wasteFlag = liveItemWaste > 0;
                        return (
                          <>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums border-l border-[var(--border)] ${r.received > 0 ? "text-sky-300" : "text-red-400"}`} data-testid={`dpe-received-${r.rm_code}`} title="Received from RM Store / previous stage">{fmt(r.received)}</td>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums ${isLegacyRow ? "text-amber-400/90 italic" : (isDirty ? "text-red-200 font-semibold" : "text-red-300")}`} data-testid={`dpe-consumed-${r.rm_code}`} title={isLegacyRow ? (derivableLegacy ? `Legacy-derived · Consumed = Produced ${fmt(derivedConsumed)} (historical pre-canonical production; not a V2 posting)` : "Historical quantities are insufficient or inconsistent for a safe derived value.") : (isDirty ? `Prior productive ${fmt(priorConsumed)} + this entry ${fmt(curQty)}` : "Cumulative productive RM consumption (authoritative posting)")}>
                              {isLegacyRow ? (
                                derivableLegacy
                                  ? <span data-testid={`dpe-consumed-legacy-${r.rm_code}`}>{fmt(derivedConsumed)}</span>
                                  : <span data-testid={`dpe-consumed-dash-${r.rm_code}`}>n/a</span>
                              ) : fmt(liveConsumed)}
                            </td>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums ${isLegacyRow ? "text-amber-400/90 italic" : (liveBalance > 0 ? "text-sky-200" : "text-red-400")}`} data-testid={`dpe-balance-avail-${r.rm_code}`} title={isLegacyRow ? (derivableLegacy ? `Legacy-derived · Received ${fmt(legacyReceived)} − Produced ${fmt(legacyProduced)} − Wastage ${fmt(legacyWastage)} = ${fmt(derivedBalance)} (historical pre-canonical; not a V2 posting)` : "Historical quantities are insufficient or inconsistent for a safe derived balance.") : `Received ${fmt(r.received)} − Consumed ${fmt(priorConsumed + curQty)} − Wastage ${fmt(priorWastage + curWastage)}`}>
                              {isLegacyRow ? (
                                derivableLegacy
                                  ? <span data-testid={`dpe-balance-avail-legacy-${r.rm_code}`}>{fmt(derivedBalance)}</span>
                                  : <span data-testid={`dpe-balance-avail-dash-${r.rm_code}`}>n/a</span>
                              ) : fmt(liveBalance)}
                            </td>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums ${wasteFlag ? "text-red-300" : "text-zinc-500"}`} data-testid={`dpe-item-waste-${r.rm_code}`} title={`Cumulative wastage across every SO/stage: ${fmt(r.item_waste_total)} kg over ${r.item_waste_count} entr${r.item_waste_count === 1 ? "y" : "ies"} (avg ${r.item_waste_pct.toFixed(2)}%)${curWastage > 0 ? ` + today ${fmt(curWastage)}` : ""}`}>{fmt(liveItemWaste)}</td>
                          </>
                        );
                      })()}
                      {dept === "WARPING" && (
                        /* Phase 3J.9C · SFW Produced to Date · spec §10 · sums
                           production entries filtered to (cwo_id, rm_code).
                           Includes both canonical + legacy history (owner's
                           historical 50 kg on WN-3456 remains visible). */
                        <td className={`px-3 py-2 text-right font-mono tabular-nums ${r.sfw_produced_to_date > 0 ? "text-cyan-300" : "text-zinc-500"}`} data-testid={`dpe-sfw-produced-${r.rm_code}`} title={r.consumption_status === "LEGACY_PRE_CANONICAL" ? "Historical production quantity; not necessarily canonical V2 SFW stock." : "Cumulative SFW output posted against this exact CWO conversion line"}>{fmt(r.sfw_produced_to_date)}</td>
                      )}
                      <td className="px-3 py-2 text-right border-l border-[var(--border)]">
                        <input type="number" step="0.001" min="0" max={maxProduced}
                          value={lineQtys[r.rm_code] ?? ""}
                          onChange={e => setLineQtys(q => ({ ...q, [r.rm_code]: e.target.value }))}
                          disabled={r.balance <= 0 || sfMissing || noStock}
                          placeholder="0.000"
                          title={noStock ? `Nothing received yet at ${(DEPTS.find(d => d.code === dept) || {}).label} floor — issue material first` : (isSw ? `Max ${fmt(maxProduced)} ${r.uom || "kg"} · MIN(remaining ${fmt(r.balance)}, SFC available ${fmt(spxAvail)} − wastage ${fmt(curWastage)})` : `Max ${fmt(maxProduced)} ${r.uom || "kg"} · floor ${fmt(r.floor_balance)} − wastage ${fmt(curWastage)}`)}
                          className="w-32 h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right font-mono tabular-nums text-white text-base focus:outline-none focus:border-[var(--accent)] disabled:opacity-40"
                          data-testid={`dpe-qty-${r.rm_code}`} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" step="0.001" min="0" max={maxWastage}
                          value={lineWastage[r.rm_code] ?? ""}
                          onChange={e => setLineWastage(w => ({ ...w, [r.rm_code]: e.target.value }))}
                          disabled={isSw ? sfMissing : noStock}
                          placeholder="0.000"
                          title={isSw
                            ? `New-entry wastage · Max ${fmt(maxWastage)} ${r.uom || "kg"} · SFC available ${fmt(spxAvail)} − produced ${fmt(curQty)}`
                            : `Max ${fmt(maxWastage)} ${r.uom || "kg"} · floor ${fmt(r.floor_balance)} − produced ${fmt(curQty)}`}
                          className="w-28 h-10 px-3 bg-[var(--bg-0)] border border-red-500/30 rounded-sm text-right font-mono tabular-nums text-red-200 text-base focus:outline-none focus:border-red-400 disabled:opacity-40"
                          data-testid={`dpe-wastage-${r.rm_code}`} />
                      </td>
                      <td className="px-3 py-2 text-zinc-400">{r.uom || "kg"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pickedCwo && dept !== "COVERING" && rmRows.length === 0 && (
          <div className="text-[11px] font-mono text-amber-300">This Child WO has no lines — re-run Explode BOM on the parent WO.</div>
        )}

        {/* Feb 2026 · Phase 2 · Cumulative CWO Tracking strip (always visible for context)
            Sep 2026 · Phase 3J.17 · gated OFF for COVERING (new Output SF card
            already surfaces planned/produced/balance for the Covering CWO). */}
        {dept !== "COVERING" && dept !== "FINISHING" && (() => {
          const hasData = pickedCwo && rmRows.length > 0;
          const plannedTot = hasData ? rmRows.reduce((s, r) => s + num(r.planned), 0) : 0;
          const producedTot = hasData ? rmRows.reduce((s, r) => s + num(r.produced), 0) : 0;
          const balanceTot = Math.max(0, plannedTot - producedTot);
          const cumWastage = hasData
            ? (entries || []).filter(e => e.cwo_id === pickedCwo.id).reduce((s, e) => s + num(e.wastage_qty), 0)
            : 0;
          const wPct = (producedTot + cumWastage) > 0
            ? (cumWastage / (producedTot + cumWastage)) * 100 : 0;
          const cells = [
            ["Planned", plannedTot, "text-white"],
            ["Produced", producedTot, "text-emerald-300"],
            ["Balance", balanceTot, "text-amber-300"],
            ["Cum Wastage", cumWastage, "text-red-300"],
            ["Actual Wastage %", wPct.toFixed(2), "text-red-200"],
          ];
          return (
            <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/40" data-testid="dpe-cumulative">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Cumulative CWO Tracking</div>
                {!hasData && <span className="text-[10px] font-mono text-zinc-500">— pick a Child WO to populate —</span>}
              </div>
              <div className="grid grid-cols-5 gap-3">
                {cells.map(([label, val, cls]) => (
                  <div key={label}>
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
                    <div className={`font-mono tabular-nums text-lg ${hasData ? cls : "text-zinc-600"}`}>
                      {hasData ? `${fmt(val)}${String(label).endsWith("%") ? "%" : ""}` : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Mar 2, 2026 · Wastage Qty moved to a per-line column in the grid
            above. This strip now shows only the totals + Reason / Approval
            (approval remains a single justification per submission).
            Sep 2026 · Phase 3J.17 · gated OFF for COVERING (new
            Production Quantity panel surfaces wastage totals). */}
        {dept !== "COVERING" && dept !== "FINISHING" && (() => {
          const hasData = pickedCwo && rmRows.length > 0;
          const activeRow = hasData
            ? (rmRows.find(r => num(lineQtys[r.rm_code]) > 0) || rmRows[0])
            : null;
          const stdPct = num(activeRow?.wastage_pct);
          const totalProduced = rmRows.reduce((s, r) => s + num(lineQtys[r.rm_code]), 0);
          const totalWastage = rmRows.reduce((s, r) => s + num(lineWastage[r.rm_code]), 0);
          const allowed = totalProduced * stdPct / 100;
          const excess = totalWastage - allowed;
          const needsApproval = totalWastage > 0 && excess > 0.0001;
          return (
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 space-y-3" data-testid="dpe-wastage">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300">Wastage</div>
                {!hasData && <span className="text-[10px] font-mono text-zinc-500">— pick a Child WO to record wastage —</span>}
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 space-y-1">
                  <div>Total Wastage (Σ lines)</div>
                  <div className="h-10 px-3 border border-red-500/30 rounded-sm flex items-center text-red-200 font-mono" data-testid="dpe-wastage-qty">{hasData ? fmt(totalWastage) : "—"}</div>
                </div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 space-y-1 block">
                  <div>Wastage Reason</div>
                  <input data-testid="dpe-wastage-reason" value={wastageReason}
                    onChange={e => setWastageReason(e.target.value)}
                    disabled={!hasData} placeholder="Optional"
                    className="w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)] disabled:opacity-40" />
                </label>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 space-y-1">
                  <div>Std Wastage % (from BOM)</div>
                  <div className="h-10 px-3 border border-[var(--border)] rounded-sm flex items-center text-zinc-300 font-mono">{hasData ? `${stdPct}%` : "—"}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 space-y-1">
                  <div>Allowed Wastage</div>
                  <div className="h-10 px-3 border border-[var(--border)] rounded-sm flex items-center text-zinc-300 font-mono">{hasData && totalProduced > 0 ? `${fmt(allowed)} ${activeRow?.uom || "kg"}` : "—"}</div>
                </div>
              </div>
              {needsApproval && (
                <div className="space-y-3">
                  <div className="text-[11px] font-mono text-red-300" data-testid="dpe-wastage-warning">
                    ⚠ Actual wastage exceeds standard allowance by {fmt(excess)} {activeRow?.uom || "kg"} — approval required.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 space-y-1 block">
                      <div>Approved By *</div>
                      <input data-testid="dpe-approved-by" value={approvedBy}
                        onChange={e => setApprovedBy(e.target.value)} placeholder="Approver name / email"
                        className="w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]" />
                    </label>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 space-y-1 block">
                      <div>Approval Reason *</div>
                      <input data-testid="dpe-approval-reason" value={approvalReason}
                        onChange={e => setApprovalReason(e.target.value)} placeholder="Why exceeds allowance?"
                        className="w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]" />
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        <div className="flex justify-end gap-3">
          {dept !== "FINISHING" && (
          <>
          <button onClick={resetForm} type="button"
            className="h-12 px-6 rounded-sm border border-[var(--border)] text-zinc-300 hover:bg-[var(--bg-2)] text-base font-semibold flex items-center gap-2"
            data-testid="dpe-cancel">
            Cancel
          </button>
          <button onClick={submit} disabled={!cwoId}
            className="h-12 px-6 rounded-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-base font-semibold flex items-center gap-2"
            data-testid="dpe-submit">
            <CheckCircleIcon size={16} weight="bold" />Save Production
          </button>
          </>
          )}
        </div>
      </div>

      {/* P17-UAT-12 REV-A2 · The per-date recap + reverse/delete actions have
          moved to the dedicated read-only Production Entry Registers
          (Production & Planning → Warping/Covering/Spandex Warping Register),
          which provide View / Print / Reverse with full period reporting. */}
      <div className="tactical-card px-5 py-4 text-sm text-zinc-400 flex items-center gap-2" data-testid="dpe-recap-moved-note">
        <ClockCounterClockwiseIcon size={14} weight="bold" />
        Recorded entries, reversal and printing now live in the
        <span className="text-[var(--accent)] font-semibold">{active.label} Production Entry Register</span>
        (Production &amp; Planning → Reports).
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
